import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  alpha,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Search, GitHub, Description, Web } from '@mui/icons-material';
import { motion } from 'framer-motion';

interface BaseTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
  title: string;
  emoji?: string;
  children: React.ReactNode;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
}

const BaseTab: React.FC<BaseTabProps> = ({
  isDark,
  colors,
  discordFont,
  glassStyle,
  lokiGradient,
  goldColor,
  isMobile,
  title,
  emoji,
  children,
  searchQuery,
  setSearchQuery,
}) => {
  const muiTheme = useMuiTheme();
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const inputStyles = {
    disableUnderline: true,
    style: {
      borderRadius: '12px',
      backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
      color: colors.textPrimary,
      paddingTop: '10px',
      paddingBottom: '10px',
      height: 'auto',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        {/* Header */}
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 3, 
            ...discordFont, 
            color: colors.textPrimary,
            fontSize: isMobileView ? '1.75rem' : '2.5rem',
            textAlign: 'left',
            background: lokiGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {emoji && `${emoji} `}{title}
        </Typography>

        {/* Search Bar (only if searchQuery prop is provided) */}
        {searchQuery !== undefined && setSearchQuery && (
          <Paper 
            elevation={0} 
            sx={{ 
              mb: 4, 
              p: 2, 
              borderRadius: '16px',
              bgcolor: colors.cardBg,
              ...glassStyle,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, color: colors.textSecondary }}>
                    <Search fontSize="small" />
                  </Box>
                ),
                ...inputStyles,
              }}
            />
          </Paper>
        )}

        {/* Content */}
        <Box sx={{ mb: 4 }}>
          {children}
        </Box>

        {/* Quick Links */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            bgcolor: colors.cardBg,
            ...glassStyle,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: goldColor }}>
            Quick Links
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GitHub />}
                href="https://github.com/Kaiftokare19/WFHCafeDev-Frontend"
                target="_blank"
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: colors.borderColor,
                  color: colors.textPrimary,
                  '&:hover': {
                    borderColor: goldColor,
                    color: goldColor,
                  },
                }}
              >
                Frontend Repo
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GitHub />}
                href="https://github.com/Kaiftokare19/WFHCafeDev-Backend"
                target="_blank"
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: colors.borderColor,
                  color: colors.textPrimary,
                  '&:hover': {
                    borderColor: goldColor,
                    color: goldColor,
                  },
                }}
              >
                Backend Repo
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Web />}
                href="https://wfhcafe.vercel.app"
                target="_blank"
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: colors.borderColor,
                  color: colors.textPrimary,
                  '&:hover': {
                    borderColor: goldColor,
                    color: goldColor,
                  },
                }}
              >
                Live Demo
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Description />}
                href="https://x.com/WFHCafee"
                target="_blank"
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: colors.borderColor,
                  color: colors.textPrimary,
                  '&:hover': {
                    borderColor: goldColor,
                    color: goldColor,
                  },
                }}
              >
                Follow Updates
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </motion.div>
  );
};

export default BaseTab;